/** Autók száma márkánként — case-insensitive egyezés. */
export function buildMakeCounts(cars) {
  const counts = {};
  for (const car of cars || []) {
    const key = (car.make || "").trim();
    if (!key) continue;
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

export function countForMake(make, counts) {
  if (!make || !counts) return 0;
  if (counts[make]) return counts[make];
  const norm = make.toLowerCase();
  const hit = Object.entries(counts).find(([k]) => k.toLowerCase() === norm);
  return hit ? hit[1] : 0;
}

export const PRICE_FILTER_STEP = 100_000;

const PRICE_FALLBACK_MIN = 0;
const PRICE_FALLBACK_MAX = 20_000_000;

/** Árcsúszka min/max a tényleges készlet áraiból (100 ezer Ft-ra kerekítve). */
export function computePriceSliderBounds(cars) {
  const prices = (cars || [])
    .map((c) => Number(c?.price))
    .filter((p) => Number.isFinite(p) && p >= 0);
  const step = PRICE_FILTER_STEP;
  if (prices.length === 0) {
    return { min: PRICE_FALLBACK_MIN, max: PRICE_FALLBACK_MAX, step };
  }
  const rawMin = Math.min(...prices);
  const rawMax = Math.max(...prices);
  let min = Math.floor(rawMin / step) * step;
  let max = Math.ceil(rawMax / step) * step;
  if (max <= min) max = min + step;
  return { min, max, step };
}

/** Milliós vonalzás a csúszka alatt (M címkék) — csak a tartományon belüli egész milliók. */
export function priceSliderMillionTicks(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  const ticks = [];
  const startM = Math.floor(min / 1_000_000);
  const endM = Math.ceil(max / 1_000_000);
  for (let m = Math.max(1, startM); m <= endM; m++) {
    const v = m * 1_000_000;
    if (v >= min && v <= max) ticks.push(v);
  }
  return ticks;
}

/** Hero / összegző sáv: nyilvános készlet darabszáma és árintervallum (Ft). */
export function computeInventoryStats(cars) {
  const list = Array.isArray(cars) ? cars : [];
  const prices = list
    .map((c) => Number(c?.price))
    .filter((p) => Number.isFinite(p) && p > 0);
  const count = list.length;
  let priceRange = null;
  if (prices.length === 1) {
    priceRange = { min: prices[0], max: prices[0] };
  } else if (prices.length > 1) {
    priceRange = { min: Math.min(...prices), max: Math.max(...prices) };
  }
  return { count, priceRange };
}

/** Kompakt megjelenítés pl. 2,5M–14M (hero). */
export function formatCompactPriceRange(min, max) {
  const fmt = (n) => {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) {
      const m = n / 1_000_000;
      const rounded = Math.round(m * 10) / 10;
      const body = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1).replace(".", ",");
      return `${body}M`;
    }
    if (abs >= 1_000) return `${Math.round(n / 1_000)} ezer`;
    return new Intl.NumberFormat("hu-HU").format(n);
  };
  if (min == null || max == null) return "—";
  if (min === max) return fmt(min);
  return `${fmt(min)}–${fmt(max)}`;
}

/** Katalógus márkák + tényleges készlet darabszám (szűrő UI-hoz). */
export function makesWithCounts(catalogMakes, cars) {
  const counts = buildMakeCounts(cars);
  return catalogMakes
    .map((make) => ({ make, count: countForMake(make, counts) }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => a.make.localeCompare(b.make, "hu"));
}

/** Nyilvános készlet — minden márka a listából (hero / márkasáv összesen ugyanaz). */
export function inventoryMakesWithCounts(cars) {
  const counts = buildMakeCounts(cars);
  return Object.entries(counts)
    .map(([make, count]) => ({ make, count }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => a.make.localeCompare(b.make, "hu"));
}
